import { MastheadCard } from '@/components/layout/MastheadCard'
import { PAGE_COLUMN } from '@/components/layout/pageLayout'
import { SectionHeading, GroupLabel } from '@/components/layout/SectionHeading'
import { useState } from 'react'
import { showToast } from '@/hooks/useToast'
import { useIsAppAdmin } from '@/hooks/useIsAppAdmin'
import { useWaitlistAdmin } from '@/hooks/useWaitlistAdmin'
import { useFamilyMembers } from '@/hooks/useFamilyMembers'
import { useHouseholdInvitations } from '@/hooks/useHouseholdInvitations'
import { CalendarSettings } from './CalendarSettings'
import { WaitlistAdmin } from './WaitlistAdmin'
import { PlacePicker } from './PlacePicker'
import { useTextSize } from '@/hooks/useTextSize'
import { HomeAddressSettings } from './HomeAddressSettings'
import { PlanningRhythmSettings } from './PlanningRhythmSettings'
import { SeasonsSettings } from './SeasonsSettings'
import { DemoControls } from './DemoControls'
import { SchoolMailCard } from './SchoolMailCard'
import { DisplaysSection } from './DisplaysSection'
import { SignOutSection } from './SignOutSection'
import { TestAccountReset } from './TestAccountReset'
import type { FamilyMember } from '@/types/family'

interface SettingsPageProps {
  onBack: () => void
  onFamilyMembersChanged?: () => void
}

type Tab = 'general' | 'calendar' | 'admin'

interface DeleteConfirmationProps {
  memberName: string
  onConfirm: () => void
  onCancel: () => void
  isDeleting: boolean
}

function DeleteConfirmation({ memberName, onConfirm, onCancel, isDeleting }: DeleteConfirmationProps) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-bg-elevated rounded-2xl shadow-xl max-w-sm w-full p-6 animate-scale-up">
        <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
          <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        </div>
        <h3 className="text-lg font-semibold text-neutral-800 text-center mb-2">
          Remove {memberName}?
        </h3>
        <p className="text-sm text-neutral-500 text-center mb-6">
          {memberName} will be taken off every task, routine and event note they're assigned to. This action cannot be undone.
        </p>
        <div className="flex gap-3">
          <button
            onClick={onCancel}
            disabled={isDeleting}
            className="btn-secondary flex-1 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={isDeleting}
            className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50"
          >
            {isDeleting ? 'Removing...' : 'Remove'}
          </button>
        </div>
      </div>
    </div>
  )
}

const COLORS = [
  { value: 'blue', bg: 'bg-blue-500', label: 'Blue' },
  { value: 'purple', bg: 'bg-purple-500', label: 'Purple' },
  { value: 'green', bg: 'bg-green-500', label: 'Green' },
  { value: 'orange', bg: 'bg-orange-500', label: 'Orange' },
  { value: 'pink', bg: 'bg-pink-500', label: 'Pink' },
  { value: 'teal', bg: 'bg-teal-500', label: 'Teal' },
]

function getColorClass(color: string): string {
  const colorMap: Record<string, string> = {
    blue: 'bg-blue-500',
    purple: 'bg-purple-500',
    green: 'bg-green-500',
    orange: 'bg-orange-500',
    pink: 'bg-pink-500',
    teal: 'bg-teal-500',
  }
  return colorMap[color] || 'bg-neutral-400'
}

function generateInitials(name: string): string {
  const words = name.trim().split(/\s+/)
  if (words.length === 1) {
    return words[0].substring(0, 2).toUpperCase()
  }
  return (words[0][0] + words[words.length - 1][0]).toUpperCase()
}

export function SettingsPage({
  onBack,
  onFamilyMembersChanged,
}: SettingsPageProps) {
  const { members, addMember, updateMember, deleteMember } = useFamilyMembers()
  const { invitations, createInvitation, deleteInvitation } = useHouseholdInvitations()
  const [activeTab, setActiveTab] = useState<Tab>(() => new URLSearchParams(window.location.search).get('tab') === 'calendar' ? 'calendar' : 'general')
  // The Admin tab carries demo controls and the waitlist roster. It rendered
  // for every signed-in user until the 2026-09-04 launch rehearsal; the
  // waitlist RLS was the real hole and is fixed in a migration, but a tab
  // whose panels would now come back empty should not be offered at all.
  const { isAdmin: isAppAdmin } = useIsAppAdmin()
  const { rows: waitlistAdminRows, approve: approveWaitlistRow, approveError: waitlistApproveError } = useWaitlistAdmin()

  // Add member state
  const [isAdding, setIsAdding] = useState(false)
  const [newName, setNewName] = useState('')
  const [newColor, setNewColor] = useState('blue')
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Invite partner state
  const [isInviting, setIsInviting] = useState(false)
  const [inviteEmail, setInviteEmail] = useState('')
  const [inviteError, setInviteError] = useState<string | null>(null)
  const [inviteCopied, setInviteCopied] = useState<string | null>(null)

  // Edit member state
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editName, setEditName] = useState('')
  const [editColor, setEditColor] = useState('blue')

  // Delete confirmation state
  const [deletingMember, setDeletingMember] = useState<FamilyMember | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)
  const [deleteSuccess, setDeleteSuccess] = useState<string | null>(null)

  const handleAdd = async () => {
    if (!newName.trim() || isSubmitting) return

    setIsSubmitting(true)
    try {
      await addMember({
        name: newName.trim(),
        initials: generateInitials(newName),
        color: newColor,
        is_full_user: false,
        display_order: members.length,
        avatar_url: null,
        member_type: 'core',
      })
      setNewName('')
      setNewColor('blue')
      setIsAdding(false)
      // Notify parent to refresh family members
      onFamilyMembersChanged?.()
    } catch (err) {
      console.error('Failed to add member:', err)
      showToast(`Couldn't add ${newName.trim()}. Please try again.`, 'error')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleDeleteClick = (member: FamilyMember) => {
    if (member.is_full_user) return // Can't delete main user
    setDeletingMember(member)
  }

  const handleConfirmDelete = async () => {
    if (!deletingMember) return

    setIsDeleting(true)
    try {
      await deleteMember(deletingMember.id)
      setDeleteSuccess(`${deletingMember.name} has been removed`)
      setTimeout(() => setDeleteSuccess(null), 3000)
      // Notify parent to refresh family members
      onFamilyMembersChanged?.()
    } catch (err) {
      console.error('Failed to delete member:', err)
      // The removal is one database statement (its assignment cleanup rolls
      // back with it), so a failure means nothing changed.
      const failure = err as { userFacing?: boolean; message?: string }
      const why = failure?.userFacing && failure.message ? ` ${failure.message}.` : ''
      showToast(`Couldn't remove ${deletingMember.name}.${why} Nothing was changed.`, 'error', 10000)
    } finally {
      setIsDeleting(false)
      setDeletingMember(null)
    }
  }

  const handleCancelDelete = () => {
    setDeletingMember(null)
  }

  const startEditing = (member: FamilyMember) => {
    setEditingId(member.id)
    setEditName(member.name)
    setEditColor(member.color)
  }

  const cancelEditing = () => {
    setEditingId(null)
    setEditName('')
    setEditColor('blue')
  }

  const handleSaveEdit = async () => {
    if (!editingId || !editName.trim() || isSubmitting) return

    setIsSubmitting(true)
    try {
      await updateMember(editingId, {
        name: editName.trim(),
        initials: generateInitials(editName),
        color: editColor,
      })
      cancelEditing()
    } catch (err) {
      console.error('Failed to update member:', err)
      showToast("Couldn't save that change. Your edit is still open.", 'error')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleInvite = async () => {
    if (!inviteEmail.trim() || isSubmitting) return
    setIsSubmitting(true)
    setInviteError(null)

    try {
      const inv = await createInvitation(inviteEmail)
      if (inv) {
        const link = `${window.location.origin}/join/${inv.token}`
        await navigator.clipboard.writeText(link)
        setInviteCopied(inv.token)
        setTimeout(() => setInviteCopied(null), 3000)
      }
      setInviteEmail('')
      setIsInviting(false)
    } catch (err) {
      setInviteError(err instanceof Error ? err.message : 'Failed to create invitation')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleCopyLink = async (token: string) => {
    const link = `${window.location.origin}/join/${token}`
    await navigator.clipboard.writeText(link)
    setInviteCopied(token)
    setTimeout(() => setInviteCopied(null), 3000)
  }

  const { largeText, setLargeText } = useTextSize()

  const mainUser = members.find(m => m.is_full_user)
  const otherMembers = members.filter(m => !m.is_full_user)

  const allTabs: { id: Tab; label: string; icon: React.ReactNode }[] = [
    {
      id: 'general',
      label: 'General',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" />
        </svg>
      ),
    },
    {
      id: 'calendar',
      label: 'Calendar',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
        </svg>
      ),
    },
    {
      id: 'admin',
      label: 'Admin',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
        </svg>
      ),
    },
  ]

  const tabs = allTabs.filter((t) => t.id !== 'admin' || isAppAdmin)

  return (
    <div className="h-full overflow-auto">
      <div className={PAGE_COLUMN}>
        <MastheadCard
          variant="page"
          title="Settings"
          motif="settings"
          eyebrow={
            <button
              onClick={onBack}
              aria-label="Back"
              className="flex items-center gap-1.5 p-1.5 -ml-1.5 rounded-lg text-[12px] font-semibold uppercase tracking-[0.08em] text-neutral-400 hover:text-neutral-700 hover:bg-neutral-100 transition-colors"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
              </svg>
              Back
            </button>
          }
        />

        {/* Tab Navigation */}
        <div className="mb-8 flex gap-1 border-b border-neutral-300">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`-mb-px flex flex-1 items-center justify-center gap-2 border-b-2 px-4 py-2.5 text-[14px] font-medium transition-colors ${
                activeTab === tab.id
                  ? 'border-accent-500 text-neutral-900'
                  : 'border-transparent text-neutral-500 hover:text-neutral-700'
              }`}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {activeTab === 'general' && (
          <div className="space-y-8">
            {/* Your place IS the theme: artwork + accent colours (the separate
                Appearance chooser was retired, 2026-09-23). */}
            <PlacePicker />

            {/* Text Size */}
            <section>
              <SectionHeading>Text Size</SectionHeading>
              <div className="flex items-center justify-between gap-4 py-3">
                <div>
                  <p className="text-[16px] leading-snug text-neutral-900">Large text</p>
                  <p className="mt-0.5 text-[14px] text-neutral-500">Increase font sizes for easier reading</p>
                </div>
                <button
                  onClick={() => setLargeText(!largeText)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full shrink-0 transition-colors duration-200 ${
                    largeText ? 'bg-primary-500' : 'bg-neutral-200'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform duration-200 ${
                      largeText ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </section>

            {/* Home Address */}
            <HomeAddressSettings />

            {/* Planning Rhythm (W4 — cadence config) */}
            <PlanningRhythmSettings />

            {/* Seasons — the household's own boundaries; every "season" in the app follows them */}
            <SeasonsSettings />

            {/* People Section */}
            <section>
              <SectionHeading>People</SectionHeading>
              <p className="text-sm text-neutral-500 mb-6">
                Add family members to assign tasks, routines, and events. People don't need an account to be assignees.
              </p>

              {/* Main user */}
              {mainUser && (
                <div className="mb-4">
                  <GroupLabel as="div">You</GroupLabel>
                  {editingId === mainUser.id ? (
                    <div className="p-4 bg-neutral-50 rounded-lg border border-neutral-200">
                      <input
                        type="text"
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        aria-label="Your name"
                        placeholder="Name"
                        className="input-base w-full mb-3"
                        autoFocus
                      />

                      <div className="flex gap-2 mb-4">
                        {COLORS.map((color) => (
                          <button
                            key={color.value}
                            onClick={() => setEditColor(color.value)}
                            className={`w-8 h-8 rounded-full ${color.bg} transition-transform ${
                              editColor === color.value ? 'ring-2 ring-offset-2 ring-neutral-400 scale-110' : ''
                            }`}
                            title={color.label}
                            aria-label={`Color: ${color.label}`}
                          />
                        ))}
                      </div>

                      <div className="flex gap-2">
                        <button
                          onClick={cancelEditing}
                          className="btn-secondary flex-1"
                          disabled={isSubmitting}
                        >
                          Cancel
                        </button>
                        <button
                          onClick={handleSaveEdit}
                          disabled={!editName.trim() || isSubmitting}
                          className="btn-primary flex-1 disabled:opacity-50"
                        >
                          {isSubmitting ? 'Saving...' : 'Save'}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center gap-3 py-2.5">
                      <div className={`w-10 h-10 rounded-full ${getColorClass(mainUser.color)} flex items-center justify-center text-white font-medium`}>
                        {mainUser.initials}
                      </div>
                      <span className="flex-1 min-w-0 truncate text-[16px] text-neutral-900">{mainUser.name}</span>
                      <span className="text-xs text-primary-600 bg-primary-50 px-2 py-1 rounded">Account owner</span>
                      <button
                        onClick={() => startEditing(mainUser)}
                        className="text-neutral-400 hover:text-neutral-600 p-1"
                        title="Edit"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 20 20" fill="currentColor">
                          <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                        </svg>
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Family members */}
              <div>
                <GroupLabel as="div">Family members</GroupLabel>

                {otherMembers.length > 0 ? (
                  <ul className="mb-4">
                    {otherMembers.map((member) => (
                      <li key={member.id}>
                        {editingId === member.id ? (
                          <div className="p-4 bg-neutral-50 rounded-lg border border-neutral-200">
                            <input
                              type="text"
                              value={editName}
                              onChange={(e) => setEditName(e.target.value)}
                              aria-label={`${member.name}'s name`}
                              placeholder="Name"
                              className="input-base w-full mb-3"
                              autoFocus
                            />

                            <div className="flex gap-2 mb-4">
                              {COLORS.map((color) => (
                                <button
                                  key={color.value}
                                  onClick={() => setEditColor(color.value)}
                                  className={`w-8 h-8 rounded-full ${color.bg} transition-transform ${
                                    editColor === color.value ? 'ring-2 ring-offset-2 ring-neutral-400 scale-110' : ''
                                  }`}
                                  title={color.label}
                                  aria-label={`Color: ${color.label}`}
                                />
                              ))}
                            </div>

                            <div className="flex gap-2">
                              <button
                                onClick={cancelEditing}
                                className="btn-secondary flex-1"
                                disabled={isSubmitting}
                              >
                                Cancel
                              </button>
                              <button
                                onClick={handleSaveEdit}
                                disabled={!editName.trim() || isSubmitting}
                                className="btn-primary flex-1 disabled:opacity-50"
                              >
                                {isSubmitting ? 'Saving...' : 'Save'}
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-center gap-3 py-2.5">
                            <div className={`w-10 h-10 rounded-full ${getColorClass(member.color)} flex items-center justify-center text-white font-medium`}>
                              {member.initials}
                            </div>
                            <span className="flex-1 min-w-0 truncate text-[16px] text-neutral-900">{member.name}</span>
                            <button
                              onClick={() => startEditing(member)}
                              className="text-neutral-400 hover:text-neutral-600 p-1"
                              title="Edit"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 20 20" fill="currentColor">
                                <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                              </svg>
                            </button>
                            <button
                              onClick={() => handleDeleteClick(member)}
                              className="text-neutral-400 hover:text-red-500 p-1"
                              title="Remove"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                              </svg>
                            </button>
                          </div>
                        )}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="mb-4 py-2 text-[14px] text-neutral-500">
                    No family members added yet
                  </p>
                )}

                {/* Add member form */}
                {isAdding ? (
                  <div className="p-4 bg-neutral-50 rounded-lg border border-neutral-200">
                    <input
                      type="text"
                      value={newName}
                      onChange={(e) => setNewName(e.target.value)}
                      aria-label="New family member's name"
                      placeholder="Name"
                      className="input-base w-full mb-3"
                      autoFocus
                    />

                    <div className="flex gap-2 mb-4">
                      {COLORS.map((color) => (
                        <button
                          key={color.value}
                          onClick={() => setNewColor(color.value)}
                          className={`w-8 h-8 rounded-full ${color.bg} transition-transform ${
                            newColor === color.value ? 'ring-2 ring-offset-2 ring-neutral-400 scale-110' : ''
                          }`}
                          title={color.label}
                          aria-label={`Color: ${color.label}`}
                        />
                      ))}
                    </div>

                    <div className="flex gap-2">
                      <button
                        onClick={() => {
                          setIsAdding(false)
                          setNewName('')
                        }}
                        className="btn-secondary flex-1"
                        disabled={isSubmitting}
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleAdd}
                        disabled={!newName.trim() || isSubmitting}
                        className="btn-primary flex-1 disabled:opacity-50"
                      >
                        {isSubmitting ? 'Adding...' : 'Add'}
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    onClick={() => setIsAdding(true)}
                    className="w-full p-3 border-2 border-dashed border-neutral-200 rounded-lg text-neutral-500 hover:border-primary-300 hover:text-primary-600 transition-colors"
                  >
                    + Add family member
                  </button>
                )}
              </div>

              {/* Invite Partner Section */}
              <div className="mt-6">
                <GroupLabel as="div">Invite partner</GroupLabel>
                <p className="text-[14px] text-neutral-500 mb-3">
                  Invite someone with their own account to share tasks, events, and routines.
                </p>

                {isInviting ? (
                  <div className="p-4 bg-neutral-50 rounded-lg border border-neutral-200">
                    <input
                      type="email"
                      value={inviteEmail}
                      onChange={(e) => { setInviteEmail(e.target.value); setInviteError(null) }}
                      aria-label="Partner's email"
                      placeholder="partner@email.com"
                      className="input-base w-full mb-3"
                      autoFocus
                      onKeyDown={(e) => e.key === 'Enter' && handleInvite()}
                    />
                    {inviteError && (
                      <p className="text-xs text-red-600 mb-3">{inviteError}</p>
                    )}
                    <div className="flex gap-2">
                      <button
                        onClick={() => { setIsInviting(false); setInviteEmail(''); setInviteError(null) }}
                        className="btn-secondary flex-1"
                        disabled={isSubmitting}
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleInvite}
                        disabled={!inviteEmail.trim() || isSubmitting}
                        className="btn-primary flex-1 disabled:opacity-50"
                      >
                        {isSubmitting ? 'Creating...' : 'Create invite link'}
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    onClick={() => setIsInviting(true)}
                    className="w-full p-3 border-2 border-dashed border-primary-200 rounded-lg text-primary-600 hover:border-primary-300 hover:bg-primary-50/50 transition-colors flex items-center justify-center gap-2"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 20 20" fill="currentColor">
                      <path d="M8 9a3 3 0 100-6 3 3 0 000 6zM8 11a6 6 0 016 6H2a6 6 0 016-6zM16 7a1 1 0 10-2 0v1h-1a1 1 0 100 2h1v1a1 1 0 102 0v-1h1a1 1 0 100-2h-1V7z" />
                    </svg>
                    Invite partner
                  </button>
                )}

                {/* Pending invitations */}
                {invitations.length > 0 && (
                  <div className="mt-3">
                    {invitations.map((inv) => {
                      const isExpired = new Date(inv.expires_at) < new Date()
                      return (
                        <div
                          key={inv.id}
                          className="flex items-center gap-3 py-2.5"
                        >
                          <div className="flex-1 min-w-0">
                            <p className="text-[16px] text-neutral-900 truncate">{inv.email}</p>
                            <p className={`mt-0.5 text-[12px] ${isExpired ? 'text-danger-600' : 'text-neutral-500'}`}>
                              {isExpired ? 'Expired' : `Expires ${new Date(inv.expires_at).toLocaleDateString()}`}
                            </p>
                          </div>
                          <button
                            onClick={() => handleCopyLink(inv.token)}
                            className="text-xs font-medium text-primary-600 hover:text-primary-700 px-2 py-1 rounded hover:bg-primary-50 transition-colors whitespace-nowrap"
                            title="Copy invite link"
                          >
                            {inviteCopied === inv.token ? 'Copied!' : 'Copy link'}
                          </button>
                          <button
                            onClick={() => deleteInvitation(inv.id)}
                            className="text-neutral-400 hover:text-red-500 p-1 transition-colors"
                            title="Remove invitation"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                            </svg>
                          </button>
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>
            </section>

            {/* Displays — a kitchen wall's own restricted sign-in */}
            <DisplaysSection members={members} />

            {/* School mail — the household's forwarding address */}
            <SchoolMailCard />

          </div>
        )}

        {activeTab === 'calendar' && (
          <CalendarSettings />
        )}

        {activeTab === 'admin' && isAppAdmin && (
          <div className="space-y-8">
            <section>
              <SectionHeading>Demo Controls</SectionHeading>
              <p className="text-sm text-neutral-500 mb-6">
                Set up and reset demo data for presentations.
              </p>
              <DemoControls />
            </section>

            <section>
              <SectionHeading>Founding households</SectionHeading>
              <p className="text-sm text-neutral-500 mb-6">
                Signups are gated on an approved waitlist row. Approve one to let that email create an account.
              </p>
              {/* An approve that the database refused. Without this the row
                  simply stayed un-approved and nothing said why — an admin
                  would tell a founding household they were in while the signup
                  gate kept turning them away. */}
              {waitlistApproveError && (
                <p role="alert" className="mb-4 rounded-lg border border-red-100 bg-red-50 px-3 py-2 text-sm text-red-700">
                  Could not approve that signup — {waitlistApproveError}
                </p>
              )}
              {waitlistAdminRows.length === 0 ? (
                <p className="text-sm text-neutral-400">No signups yet.</p>
              ) : (
                <ul>
                  {waitlistAdminRows.map((row) => (
                    <li key={row.id} className="flex items-center justify-between gap-3 py-2.5">
                      <div className="min-w-0">
                        <p className="truncate text-[16px] text-neutral-900">{row.email}</p>
                        <p className="mt-0.5 text-[12px] text-neutral-500">
                          {row.createdAt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        </p>
                      </div>
                      {row.approvedAt ? (
                        <span className="shrink-0 rounded-full bg-green-50 px-2 py-1 text-xs font-medium text-green-700">
                          approved
                        </span>
                      ) : (
                        <button
                          type="button"
                          onClick={() => void approveWaitlistRow(row.id)}
                          className="shrink-0 rounded-md bg-primary-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-primary-700 transition-colors"
                        >
                          Approve
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <section>
              <SectionHeading>Waitlist</SectionHeading>
              <p className="text-sm text-neutral-500 mb-6">
                Manage waitlist signups from the landing page.
              </p>
              <WaitlistAdmin />
            </section>
          </div>
        )}

        <TestAccountReset />
        <SignOutSection />
      </div>

      {/* Delete confirmation modal */}
      {deletingMember && (
        <DeleteConfirmation
          memberName={deletingMember.name}
          onConfirm={handleConfirmDelete}
          onCancel={handleCancelDelete}
          isDeleting={isDeleting}
        />
      )}

      {/* Success toast */}
      {deleteSuccess && (
        <div className="phone-lift fixed bottom-6 left-1/2 -translate-x-1/2 z-50 animate-fade-in-up">
          <div className="flex items-center gap-2 px-4 py-3 bg-neutral-800 text-white rounded-lg shadow-lg">
            <svg className="w-5 h-5 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
            <span className="text-sm font-medium">{deleteSuccess}</span>
          </div>
        </div>
      )}
    </div>
  )
}
